define([
    'lodash',
    'react',
    'componentsCore',
    'coreUtils',
    'santa-core-utils',
    'components',
    'santa-components',
    'skins',
    'mediaPlayer/skins/skins.json'
], function (
    _,
    React,
    componentsCore,
    coreUtils,
    coreUtilsLib,
    components,
    santaComponents,
    skinsPackage,
    skinsJson
) {
    'use strict';

    const consts = coreUtils.mediaConsts;
    const mediaPlayerMixin = components.mediaCommon.mediaLogicMixins.mediaPlayer;
    const timeoutsMixin = coreUtilsLib.timersMixins.timeoutsMixin;
    const HOVER_TIMEOUT = 2000;
    const MOBILE_HOVER_TIMEOUT = 4000;
    const HOVER_ANIMATION_DURATION = 500;

    const {inlineContentMixin} = santaComponents.mixins;

    const getContainerStyle = containerStyle => {
        if (_.isEmpty(containerStyle.borderWidth)) {
            return containerStyle;
        }

        const borderWidth = parseInt(containerStyle.borderWidth, 10);
        return _.assign({}, containerStyle, {'margin': `-${borderWidth}px`});
    };

    /**
     * @class components.MediaPlayer
     * @extends {core.skinBasedComp}
     */
    const mediaPlayer = {
        displayName: 'MediaPlayer',
        mixins: [componentsCore.mixins.skinBasedComp, mediaPlayerMixin, timeoutsMixin, componentsCore.mixins.createChildComponentMixin, inlineContentMixin],

        propTypes: {
            style: santaComponents.santaTypesDefinitions.Component.style.isRequired,
            compDesign: santaComponents.santaTypesDefinitions.Component.compDesign,
            compProp: santaComponents.santaTypesDefinitions.Component.compProp,
            isMobileDevice: santaComponents.santaTypesDefinitions.Device.isMobileDevice,
            renderParts: santaComponents.santaTypesDefinitions.Media.renderParts,
            containerStyle: santaComponents.santaTypesDefinitions.MediaPlayerDesign.containerStyle,
            //renderFlags
            isMobileView: santaComponents.santaTypesDefinitions.isMobileView
        },
        statics: {
            behaviors: components.mediaCommon.mediaBehaviors.mediaPlayer
        },

        getInitialState() {
            this.timedHoverAndAnimationDuration = false;
            return {
                hover: false,
                timedHover: false
            };
        },

        /**
         * Add playerId to all the child components of the player
         * @returns {Object}
         */
        addPropsToChildren(children) {
            const props = {
                allowReplay: _.get(this.props.compProp.playerInteraction, 'allowReplay', true),
                playerInHoverInteraction: this.props.compProp.playerInteraction.rollIn !== 'none' && !this.isMobile(),
                playerInHoverState: this.state.hover,
                playerInTimedHoverState: this.state.timedHover
            };
            return React.Children.map(children, child => React.cloneElement(child, props));
        },

        /**
         * Toggle hover state to pass to children
         * @param event
         */
        toggleHover(event) {
            //call for a mixin function
            const currentState = this.getMediaState();
            const isPlayEnded = currentState.playbackState === consts.playbackTypes.PLAY_ENDED;
            const isReplayDisabled = this.props.compProp.playerInteraction.allowReplay === false;
            const isInteractionDisabled = isReplayDisabled && isPlayEnded;
            const isHoverEvent = event.type !== 'mouseleave';
            const isHover = isHoverEvent && !isInteractionDisabled;

            this.setState({
                hover: isHover
            });
            if (!isHover) {
                this.removeTimedHover();
            }
        },

        /**
         * Set a special hover that ends after a timeout period to pass to children
         * Mainly for showing controls and other visuals for a short time while playing
         */
        setTimedHover() {
            if (!this.state.timedHover) {
                this.setState({
                    timedHover: true
                }, () => {this.timedHoverAndAnimationDuration = true;});
            }

            this.setTimeout(this.removeTimedHover, this.isMobile() ? MOBILE_HOVER_TIMEOUT : HOVER_TIMEOUT);
        },

        /**
         * Unset the timed hover state
         */
        removeTimedHover() {
            this.setState({
                timedHover: false
            });
            this.setTimeout(() => {
                this.timedHoverAndAnimationDuration = false;
            }, HOVER_ANIMATION_DURATION);
        },

        /**
         * Handle mouseEnter / mouseLeave actions on player
         * @param {[{action: function, params: array}]?} actions
         * @returns {function(event: MouseEvent)}
         */
        handleHoverAction(actions) {
            return event => {
                this.toggleHover(event);
                actions.forEach(command => {
                    if (command.action) {
                        const params = command.params || [];
                        command.action(...params);
                    }
                });
            };
        },

        getAudioHoverInteraction(key) {
            const action = _.get(this.props.compProp, ['playerAudioInteraction', key]);
            const audioActions = {
                mute: {action: this.mediaVolumeFadeOut, params: [0.5]},
                unmute: {action: this.mediaVolumeFadeIn, params: [0.5]}
            };
            return audioActions[action];
        },

        getVideoHoverInteraction(key) {
            const action = this.props.compProp.playerInteraction[key];
            const videoActions = {
                play: {action: this._playMedia},
                pause: {action: this._pauseMedia},
                none: null
            };
            return videoActions[action];
        },

        getClickInteraction() {
            const {click} = this.props.compProp.playerInteraction;
            const clickActions = {
                toggle: () => this.togglePlayMedia(),
                play: () => this._playMedia({persist: true})
            };
            return clickActions[click];
        },

        /**
         * rollin/rollout interaction are converted to command actions
         * @returns {Function}
         */
        convertHoverToCommandActions() {
            if (this.props.compProp.playerInteraction.rollOut === 'pause') {
                return () => this.togglePlayMedia();
            }
            if (this.props.compProp.playerInteraction.rollIn === 'play') {
                return () => this._playMedia();
            }
            return _.noop;
        },

        /**
         * checks if to trigger actions - for mobile use
         * initial play || no audio || within hover duration
         * @returns {boolean}}
         */
        shouldTriggerAction() {
            const currentState = this.getMediaState();
            const isFirstPlay = currentState.playbackState === consts.playbackTypes.IDLE;
            const noAudio = this.props.compDesign.background.mediaRef.hasAudio === false || this.props.compProp.disableAudio;
            return this.timedHoverAndAnimationDuration || isFirstPlay || noAudio;
        },

        /**
         * handle the touch event and prevent the click from firing
         * @param commandActions
         * @returns {Function}
         */
        getTouchInteraction(commandActions) {
            return event => {
                event.preventDefault();
                this.invokeMobileInteraction(event, commandActions);
            };
        },

        /**
         * handle the click event for mobile
         * @param commandActions
         * @returns {Function}
         */
        getMobileClickInteraction(commandActions) {
            return event => {
                //event.preventDefault();
                this.invokeMobileInteraction(event, commandActions);
            };
        },

        /**
         * invocation simulates hover behavior
         * invocation will trigger the action if initial play, no audio and within hover duration
         * @param event
         * @param commandActions
         */
        invokeMobileInteraction(event, commandActions) {
            this.setTimedHover();
            if (this.shouldTriggerAction()) {
                commandActions();
            }
            this.toggleHover(event);
        },

        isMobile() {
            return this.props.isMobileView || this.props.isMobileDevice;
        },

        getColntrols() {
            const shouldRenderChildren = !!this.props.renderParts.media.video.length;
            return shouldRenderChildren ? this.getChildrenRenderer({children: this.addPropsToChildren(this.props.children)}) : null;
        },

        /**
         * @returns {*} event handlers by platform
         */
        getInteractionEvents() {
            if (this.isMobile()) {
                const clickActions = this.getClickInteraction();
                const commandActions = clickActions || this.convertHoverToCommandActions();
                const eventName = this.props.isMobileDevice ? 'onTap' : 'onClick';
                return {
                    [eventName]: this.getMobileClickInteraction(commandActions)
                };
            }

            const rollInActions = [
                this.getVideoHoverInteraction('rollIn'),
                this.getAudioHoverInteraction('rollIn')
            ];

            const rollOutActions = [
                this.getVideoHoverInteraction('rollOut'),
                this.getAudioHoverInteraction('rollOut')
            ];

            return {
                onMouseMove: this.setTimedHover,
                onClick: this.getClickInteraction(),
                onMouseEnter: this.handleHoverAction(_.compact(rollInActions)),
                onMouseLeave: this.handleHoverAction(_.compact(rollOutActions))
            };
        },

        getSkinProperties() {
            const children = this.getColntrols();
            const style = this.props.style;
            const refData = {
                '': {
                    style
                },

                background: this.createFillLayers(
                    {
                        enableVideo: !!children
                    }),

                container: {
                    style: getContainerStyle(this.props.containerStyle)
                },

                inlineContent: {
                    children
                }
            };

            _.assign(refData.container, this.getInteractionEvents());

            return refData;
        }
    };

    componentsCore.compRegistrar.register('wysiwyg.viewer.components.MediaPlayer', mediaPlayer);
    skinsPackage.skinsMap.addBatch(skinsJson);

    return mediaPlayer;
});
